import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CustomDelete, EditWithIcon } from 'components';
import { FindLoad, FindLoadItem, Images, PermissionList, RolePermission, useModal } from 'utils';
import { chatActions } from 'store';
import { getChatName, getChatAvatar, getParticipantRole, canManageGroupChat } from './constants';
import { NewMessageModal } from './newMessageModal';

const DELETE_CHART_ACTION_TYPE = 'DELETE_CHAT';
const REMOVE_MEMBER_ACTION_TYPE = 'REMOVE_CHAT_MEMBER';
const MUTE_ACTION_TYPE = 'MUTE_CHAT';

export const ChatHeader = ({ chat, currentUserId, onBack }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const [membersOpen, setMembersOpen] = useState(false);
    const [addMembersOpen, setAddMembersOpen] = useState(false);
    const menuRef = useRef(null);
    const membersRef = useRef(null);

    const chatName = getChatName(chat, currentUserId);
    const chatAvatar = getChatAvatar(chat, currentUserId);
    const role = getParticipantRole(chat, currentUserId);
    const participantCount = chat?.participants?.length || 0;

    const otherParticipant = chat?.participants?.find((p) => p.userId !== currentUserId);
    const isMuted = chat?.participants?.find((p) => p.userId === currentUserId)?.hiddenNotification;
    const muteLoadItem = FindLoadItem(MUTE_ACTION_TYPE);
    const loadMute = muteLoadItem?.params?.chatId === chat?.chatId;
    const canManage = canManageGroupChat(chat, currentUserId, RolePermission([PermissionList.ALL_ACCESS?.code]));
    // 1-on-1 chats: either side can delete. Group chats: only who can manage them (API enforces the same).
    const canDelete = !chat?.isGroup || canManage;

    const initials = chatName
        ? chatName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)
        : '?';
    const { open } = useModal();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
            if (membersRef.current && !membersRef.current.contains(e.target)) {
                setMembersOpen(false);
            }
        };
        if (menuOpen || membersOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen, membersOpen]);

    const confirmRemove = (member, memberName) => {
        setMembersOpen(false);
        open(
            <CustomDelete
                info="Remove member?"
                text={`${memberName} will be removed from "${chatName}". They will no longer see this chat or get its messages. Their account is not affected.`}
                innerText="Remove"
                handleDel={() => dispatch(chatActions.removeChatMember(chat?.chatId, member.userId))}
                actionType={REMOVE_MEMBER_ACTION_TYPE}
            />
        );
    };

    const openProfile = (item) => {
        if (item?.userType === 'staff' && RolePermission([PermissionList.STAFF_READ.code])) {
            window.location.replace(`/staff/${item?.userId}`);
        }
        if (item?.userType === 'client' && RolePermission([PermissionList.CLIENT_READ?.code])) {
            window.location.replace(`/client/${item?.userId}`);
            // navigate(`/client/${item?.userId}`);
        }
    };

    return (
        <>
            <div className="chat-header">
                <div
                    style={{ cursor: chat?.isGroup ? 'default' : 'pointer' }}
                    className="chat-header-left"
                    onClick={() => !chat?.isGroup && openProfile(otherParticipant)}
                >
                    {chatAvatar ? (
                        <img src={chatAvatar} alt={chatName} className="chat-avatar" style={{ width: '48px', height: '48px' }} />
                    ) : (
                        <div className="chat-avatar" style={{ width: '48px', height: '48px', fontSize: '16px' }}>
                            {initials}
                        </div>
                    )}
                    <div className="chat-header-info" style={{ position: 'relative' }}>
                        <h4>{chatName}</h4>
                        {chat?.isGroup ? (
                            <div ref={membersRef} style={{ position: 'relative', display: 'inline-block' }}>
                                <div onClick={() => setMembersOpen(!membersOpen)} className="flex-align-center" style={{ gap: 4 }}>
                                    <span>{participantCount} members</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="5" height="9" viewBox="0 0 5 9" fill="none">
                                        <path
                                            d="M4.81787 3.93506C5.06201 4.1792 5.06201 4.57568 4.81787 4.81982L1.06787 8.56982C0.82373 8.81396 0.427246 8.81396 0.183105 8.56982C-0.0610352 8.32568 -0.0610352 7.9292 0.183105 7.68506L3.4917 4.37646L0.185059 1.06787C-0.059082 0.82373 -0.059082 0.427246 0.185059 0.183105C0.429199 -0.0610352 0.825684 -0.0610352 1.06982 0.183105L4.81982 3.93311L4.81787 3.93506Z"
                                            fill="#6B7280"
                                        />
                                    </svg>
                                </div>

                                {membersOpen && (
                                    <div className="chat-members-dropdown">
                                        <div
                                            className="chat-members-dropdown-header flex-align-center"
                                            style={{ justifyContent: 'space-between' }}
                                        >
                                            Members
                                            {canManage && (
                                                <button
                                                    type="button"
                                                    className="chat-members-add-btn"
                                                    onClick={() => {
                                                        setMembersOpen(false);
                                                        setAddMembersOpen(true);
                                                    }}
                                                >
                                                    + Add
                                                </button>
                                            )}
                                        </div>
                                        {(chat.participants || []).map((p) => {
                                            const pName = p.user ? `${p.user.firstName || ''} ${p.user.lastName || ''}`.trim() : 'Unknown';
                                            const pInitials = pName
                                                ? pName
                                                      .split(' ')
                                                      .map((n) => n[0])
                                                      .join('')
                                                      .toUpperCase()
                                                      .slice(0, 2)
                                                : '?';
                                            const isMe = p.userId === currentUserId;
                                            return (
                                                // ADD LINK
                                                <div key={p.userId} className="chat-members-dropdown-item" onClick={() => openProfile(p)}>
                                                    {p.user?.avatar ? (
                                                        <img
                                                            src={p.user.avatar}
                                                            alt={pName}
                                                            className="chat-user-item-avatar"
                                                            style={{ minWidth: '32px' }}
                                                        />
                                                    ) : (
                                                        <div className="chat-user-item-avatar" style={{ fontSize: '12px' }}>
                                                            {pInitials}
                                                        </div>
                                                    )}
                                                    <div className="chat-user-item-info">
                                                        <p>{isMe ? `${pName} (You)` : pName}</p>
                                                        <span
                                                            style={{
                                                                textDecoration: 'none',
                                                                cursor: 'default',
                                                            }}
                                                        >
                                                            {p.userType || ''}
                                                        </span>
                                                    </div>
                                                    {canManage && !isMe && (
                                                        <button
                                                            type="button"
                                                            className="chat-members-remove-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                confirmRemove(p, pName);
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            role && <span style={{ textDecoration: 'none', cursor: 'default' }}>{role}</span>
                        )}
                    </div>
                </div>

                <div className="chat-header-actions">
                    <button
                        className="chat-header-action-btn"
                        title={isMuted ? 'Unmute notifications' : 'Mute notifications'}
                        onClick={() => {
                            if (!loadMute) {
                                dispatch(chatActions.muteChat(chat.chatId, !isMuted));
                            }
                        }}
                        disabled={loadMute}
                        style={isMuted ? { color: '#D92D20' } : {}}
                    >
                        {loadMute ? (
                            <div
                                className="chat-btn-loader"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid #e0e0e0',
                                    borderTop: `2px solid ${isMuted ? '#D92D20' : '#475467'}`,
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                }}
                            />
                        ) : isMuted ? (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M15 6.67a5 5 0 10-10 0c0 5.83-2.5 7.5-2.5 7.5h15S15 12.5 15 6.67M11.44 17.5a1.67 1.67 0 01-2.88 0"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path d="M1.67 1.67l16.66 16.66" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M15 6.67a5 5 0 10-10 0c0 5.83-2.5 7.5-2.5 7.5h15S15 12.5 15 6.67M11.44 17.5a1.67 1.67 0 01-2.88 0"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </button>
                    {/* Kebab menu — only holds Delete Chat */}
                    {canDelete && (
                        <div style={{ position: 'relative' }} ref={menuRef}>
                            <EditWithIcon
                                id={'more-options'}
                                icon={Images.moreGrayIcon}
                                type={'button'}
                                handleClick={() => setMenuOpen(!menuOpen)}
                            />
                            {menuOpen && (
                                <div className="chat-kebab-menu">
                                    {/*<button*/}
                                    {/*    className="chat-kebab-menu-item"*/}
                                    {/*    onClick={() => {*/}
                                    {/*        setMenuOpen(false);*/}
                                    {/*        if (otherParticipant) {*/}
                                    {/*            const route = otherParticipant.userType === 'client'*/}
                                    {/*                ? `/client/${otherParticipant.userId}`*/}
                                    {/*                : `/staff/${otherParticipant.userId}`;*/}
                                    {/*            navigate(route);*/}
                                    {/*        }*/}
                                    {/*    }}*/}
                                    {/*>*/}
                                    {/*    View Profile*/}
                                    {/*</button>*/}
                                    <button
                                        className="chat-kebab-menu-item chat-kebab-menu-item-danger"
                                        onClick={() => {
                                            open(
                                                <CustomDelete
                                                    info="Delete Conversation ?"
                                                    text="Are you sure you want to delete this conversation ? This action cannot be undone."
                                                    handleDel={() => dispatch(chatActions.deleteChat(chat?.chatId))}
                                                    actionType={DELETE_CHART_ACTION_TYPE}
                                                    afterSuccess={() => dispatch(chatActions.clearActiveChat())}
                                                />
                                            );
                                        }}
                                    >
                                        Delete Chat
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {canManage && (
                <NewMessageModal
                    open={addMembersOpen}
                    onClose={() => setAddMembersOpen(false)}
                    currentUserId={currentUserId}
                    addToChat={chat}
                />
            )}
        </>
    );
};
